/**
 * VisualStyleSelectionsContainer — native staging wizard column (lifted from
 * `common/StagingFlow/VisualStyleSelectionsContainer`, rewired onto the module
 * working context + plain-JS room arrays). Owns the local step cursor
 * (`visualStyleSelectionStep`). Every `activeRoom?.` optional-chain is retained
 * (a room can be removed while a popup is open). Analytics dropped.
 */

import { useCallback, useLayoutEffect, useState } from 'react'

import { BEIGE_500 } from 'constants/styling/theme'
import { BorderBoxWrapper } from 'components/common/BorderBoxWrapper'
import { MUIButton } from 'components/common/MUIButton'
import Stack from '@mui/material/Stack'
import { SystemMessage } from 'components/common/SystemMessage'
import { VirtualFurnitureRoomRemovableOption } from 'constants/virtualFurniture'
import { useTranslation } from 'react-i18next'

import { RoomItems } from './RoomItems.component'
import { RoomRemovableObjects } from './RoomRemovableObjects.component'
import { RoomTypes } from './RoomTypes.component'
import { SelectedRoomItemSection } from './SelectedRoomItemSection.component'
import { StagingSpecialRequestPopup } from './StagingSpecialRequestPopup.component'
import { StagingSpecialRequestSection } from './StagingSpecialRequestSection.component'
import { useStagingWorkingState } from '../StagingWorkingState.context'

export enum VisualStyleSelectionContainerState {
  ROOM_TYPES_AND_REMOVABLE_OPTIONS,
  ROOM_ITEMS,
}

export const VisualStyleSelectionsContainer = () => {
  const { t } = useTranslation(['style_selection_order_step'])

  const { activeRoom, isRoomItemSelected, isBKBNDecide } = useStagingWorkingState()

  const [visualStyleSelectionStep, setVisualStyleSelectionStep] = useState<VisualStyleSelectionContainerState>(() =>
    activeRoom?.roomTypes.length
      ? VisualStyleSelectionContainerState.ROOM_ITEMS
      : VisualStyleSelectionContainerState.ROOM_TYPES_AND_REMOVABLE_OPTIONS
  )

  const isRoomTypesAndOptionsStep =
    visualStyleSelectionStep === VisualStyleSelectionContainerState.ROOM_TYPES_AND_REMOVABLE_OPTIONS
  const isRoomItemsStep = visualStyleSelectionStep === VisualStyleSelectionContainerState.ROOM_ITEMS

  const isSpecialRequestComments = activeRoom?.comment

  const saveRoomType = useCallback(() => {
    setVisualStyleSelectionStep(VisualStyleSelectionContainerState.ROOM_ITEMS)
  }, [])

  useLayoutEffect(() => {
    setVisualStyleSelectionStep(
      activeRoom?.roomTypes.length && activeRoom?.furnitureRemoval?.length
        ? VisualStyleSelectionContainerState.ROOM_ITEMS
        : VisualStyleSelectionContainerState.ROOM_TYPES_AND_REMOVABLE_OPTIONS
    )
    // Only re-run when the active room identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeRoom?.key])

  // 70% is the PREFERRED width, not a floor. The room list beside this panel is
  // sized by its thumbnails (min 250px + padding ≈ 290px), so below roughly a
  // 1470px viewport `70% + 290 + gap` no longer fits the content column — and a
  // non-shrinking panel spilled the excess to the right, 40px of it at 1280,
  // sliding under the summary column. Shrinking keeps both columns inside their
  // grid cell; `minWidth: 0` stops the chip rows pushing it back out. It still
  // never GROWS past 70%, so the wide-viewport proportions are unchanged. Below
  // `lg` the pair stacks (see `Staging.component`), where this takes full width.
  return (
    <Stack gap="1.6rem" width={{ xs: '100%', lg: '70%' }} minWidth={0} boxSizing="border-box">

      <BorderBoxWrapper padding={2} height="100%" width="100%" backgroundColor={BEIGE_500} elevation="none" border="none">

        {activeRoom && isRoomTypesAndOptionsStep &&
          <Stack gap={3} alignItems="center">

            <RoomTypes />

            <RoomRemovableObjects />

            <MUIButton
              type="defaultPrimary"
              variant="contained"
              disabled={activeRoom.roomTypes.length === 0 || !activeRoom.furnitureRemoval}
              onClick={saveRoomType}
            >
              {t('style_selection_order_step:save_room_type')}
            </MUIButton>

          </Stack>
        }

        {isRoomItemsStep && activeRoom && <RoomItems setVisualStyleSelectionStep={setVisualStyleSelectionStep} />}

      </BorderBoxWrapper>

      {isRoomTypesAndOptionsStep && activeRoom?.furnitureRemoval === VirtualFurnitureRoomRemovableOption.NONE &&
        <SystemMessage
          title={t('style_selection_order_step:none_removals_title')}
          message={t('style_selection_order_step:none_removals_text')}
          variant="system"
        />
      }

      {isRoomItemsStep && activeRoom?.skipRenovations &&
        <SystemMessage
          title={t('style_selection_order_step:none_renovations_title')}
          message={t('style_selection_order_step:none_renovations_text')}
          variant="system"
        />
      }

      {isRoomItemsStep && isRoomItemSelected && !isBKBNDecide && <SelectedRoomItemSection />}

      {isRoomItemsStep && !!isSpecialRequestComments && <StagingSpecialRequestSection />}

      <StagingSpecialRequestPopup />

    </Stack>
  )
}
